import React, { useEffect, useState } from "react";
import axios from "../api/api";
import { useAuth } from "../components/AuthContext";
import "../styles/leaveApplication.css";
import Modal from "../components/showModal";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ApprovedLeavesPage = () => {
  const { role } = useAuth();
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [editingLeave, setEditingLeave] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10); // <-- add this
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState('');
  const [modalMessage, setModalMessage] = React.useState('');
  const [modalConfirmAction, setModalConfirmAction] = React.useState(null);


  const fetchApprovedLeaves = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/leave/approved");
      setApprovedLeaves(res.data);
    } catch (err) {
      console.error("Error fetching approved leaves:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.post(`/leave/update/${editingLeave.id}`, editingLeave);
      setShowEditModal(false);
      setEditingLeave(null);
      fetchApprovedLeaves();
    } catch (err) {
      console.error("Error updating leave:", err);
    }
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!window.confirm("Are you sure you want to delete this leave?")) return;

    try {
      await axios.delete(`leave/delete/${leaveId}`);
      // Remove the deleted leave from the state
      setApprovedLeaves(prev => prev.filter(leave => leave.id !== leaveId));
      alert("Leave deleted successfully");
    } catch (error) {
      console.error("Error deleting leave:", error);
      alert("Failed to delete leave");
    }
  };

  const confirmDeleteLeave = (leaveId) => {
    setModalTitle("Delete Leave");
    setModalMessage("Are you sure you want to delete this leave?");
    setModalConfirmAction(() => async () => {
      try {
        await axios.delete(`/leave/delete/${leaveId}`);

        // Refresh page
        window.location.reload();

        // Show toast
        toast.success("Leave deleted successfully");

        setModalOpen(false); // close modal
      } catch (error) {
        console.error("Error deleting leave:", error);
        setModalTitle("Error");
        setModalMessage("Failed to delete leave");
        setModalConfirmAction(null); // Only OK button for error
      }
    });
    setModalOpen(true);
  };


  useEffect(() => {
    fetchApprovedLeaves();
  }, []);

  const filteredLeaves = approvedLeaves
    .filter((leave) =>
      leave.user?.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => new Date(b.start_date) - new Date(a.start_date)); // latest first

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredLeaves.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredLeaves.length / recordsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="approved-leaves-page">
      <h2 className="approved-title">Approved Leaves</h2>
      <div className="approved-leaves-search-container">
        <input
          type="text"
          placeholder="Search employee..."
          className="search-box"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {loading ? (
        <p className="loading-text">Loading approved leaves... Please wait</p>
      ) : currentRecords.length === 0 ? (
        <p className="no-data-text">No approved leaves found.</p>
      ) : (
        <>
          <div className="approved-list">
            {currentRecords.map((leave, index) => (
              <div key={leave.id} className="approved-item">
                <div className="leave-details">
                  <p><strong>User:</strong> {leave.user?.name}</p>
                  <p><strong>Type:</strong> {leave.type}</p>
                  <p>
                    <strong>Dates:</strong>{" "}
                    {new Date(leave.start_date).toLocaleDateString("en-GB")} -{" "}
                    {new Date(leave.end_date).toLocaleDateString("en-GB")}
                  </p>
                  <p><strong>Reason:</strong> {leave.reason}</p>
                </div>
                {role === "admin" && (
                  <div className="leave-actions">
                    <button
                      className="edit-btn"
                      onClick={() => {
                        setEditingLeave(leave);
                        setShowEditModal(true);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => confirmDeleteLeave(leave.id)}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

          <Modal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            title={modalTitle}
            message={modalMessage}
            confirmLabel={modalConfirmAction ? "Delete" : "OK"}
            cancelLabel={modalConfirmAction ? "Cancel" : null}
            onConfirm={modalConfirmAction}
          />

          <div className="approved-leaves-pagination">
            <div className="page-buttons">
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => paginate(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>

            <div className="records-per-page-container">
              <label className="rpp-label">Leaves per page:</label>
              <select
                className="rpp-select"
                value={recordsPerPage}
                onChange={(e) => {
                  setRecordsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {showEditModal && editingLeave && (
        <div className="modal">
          <div className="modal-content">
            <h2>Edit Leave</h2>
            <label>Type</label>
            <select
              value={editingLeave.type}
              onChange={(e) =>
                setEditingLeave({ ...editingLeave, type: e.target.value })
              }
            >
              <option value="">Select leave type</option>
              <option value="Sick">Sick</option>
              <option value="annual">Annual</option>
              <option value="authorised">Authorised</option>
              <option value="maternity">Maternity</option>
              <option value="paternity">Paternity</option>
              <option value="bereavement">Bereavement</option>
            </select>

            <label>Start Date</label>
            <input
              type="date"
              value={editingLeave.start_date.split("T")[0]}
              onChange={(e) =>
                setEditingLeave({ ...editingLeave, start_date: e.target.value })
              }
            />
            <label>End Date</label>
            <input
              type="date"
              value={editingLeave.end_date.split("T")[0]}
              onChange={(e) =>
                setEditingLeave({ ...editingLeave, end_date: e.target.value })
              }
            />
            <label>Reason</label>
            <textarea
              value={editingLeave.reason}
              onChange={(e) =>
                setEditingLeave({ ...editingLeave, reason: e.target.value })
              }
            />

            <div className="modal-actions">
              <button className="save-btn" onClick={handleUpdate}>
                Save
              </button>
              <button
                className="cancel-btn"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovedLeavesPage;
